import { getOrder } from '@/src/router/views/schedule/hotel/api/order'
import request from '@utils/request'
import { formatHotelStayRange, resolveHotelStay } from '@utils/format-hotel-stay'

const getDefaultState = () => ({
  currentOrder: null,
  loading: false,
  error: null,
  selectedBookingRoomsList: [],
  currentMemberInfo: null,
  currentMaxAccommodates: 1,
  selectedPets: [],
  calculateOrder: null,
  priceAdjustments: [],
  addonItems: [],
  orderNote: '',
  /** 後台補登歷史住宿訂單（與日曆 GET bookings / calculate / create 帶 adminBackfill） */
  adminBackfillMode: false,
})

const state = getDefaultState()

const getters = {
  order: (state) => state.currentOrder,
  loading: (state) => state.loading,
  error: (state) => state.error,
  selectedBookingRoomsList: (state) => state.selectedBookingRoomsList,
  currentMemberInfo: (state) => state.currentMemberInfo,
  accommodationInfo: (state, getters, rootState, rootGetters) => {
    const data = state.selectedBookingRoomsList
    if (!data || data.length === 0) return []

    const showCheckoutDate = rootGetters['hotel/setting/showCheckoutDate']
    // 1. 取得日期範圍
    const stay = resolveHotelStay({ bookings: data, showCheckoutDate })
    const stayRange = formatHotelStayRange({ bookings: data, showCheckoutDate })
    const startDate = stay ? stay.checkIn.replace(/-/g, '/') : ''
    const endDate = stay ? stay.checkout.replace(/-/g, '/') : ''

    const transformCategories = (categories) => {
      const allTags = categories.flatMap((item) => item.tags.map((tag) => tag.split('|')[0].trim()))

      return [...new Set(allTags)].join('｜')
    }

    // 2. 處理房型分組聚合
    const roomGroups = data.reduce((acc, curr) => {
      const id = curr.roomId
      if (!acc[id]) {
        acc[id] = {
          name: curr.room.roomTitle,
          category: transformCategories(curr.type.categories),
          limit: `限 ${curr.type.accommodates} 隻`,
          date: [],
        }
      }

      // 放入每一天的價格與房名資訊
      acc[id].date.push({
        day: curr.date.replace(/-/g, '/'),
        roomName: curr.room.roomTitle,
        quantity: 1,
        price: curr.dateRoom.priceStartAt,
      })

      return acc
    }, {})

    // 3. 輸出為目標格式
    return [
      {
        startDate,
        endDate,
        stayRange,
        selectedRoom: Object.values(roomGroups),
      },
    ]
  },
  currentMaxAccommodates: (state) => state.currentMaxAccommodates,
  selectedPets: (state) => state.selectedPets,
  detailedBookingList: (state) => {
    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      return dateStr.replace(/-/g, '/')
    }

    if (!state.selectedBookingRoomsList || state.selectedBookingRoomsList.length === 0) {
      return []
    }
    const list = state.selectedBookingRoomsList.map((item) => ({
      date: formatDate(item.date),
      roomTitle: item.room?.roomTitle || '未知房型',
      price: item.dateRoom?.priceStartAt || 0,
      quantity: 1,
    }))

    //按照日期排序 (由早到晚)
    return list.sort((a, b) => a.date.localeCompare(b.date))
  },
  calculateOrder: (state) => state.calculateOrder,
  availableCategoryIds: (state) => {
    if (!state.selectedBookingRoomsList || state.selectedBookingRoomsList.length === 0) return []
    const ids = state.selectedBookingRoomsList.flatMap((item) => item.type.categories.map((cat) => cat.id))

    return [...new Set(ids)]
  },
  summaryBooking: (state, getters, rootState, rootGetters) => {
    if (!state.selectedBookingRoomsList || state.selectedBookingRoomsList.length === 0) return {}

    const showCheckoutDate = rootGetters['hotel/setting/showCheckoutDate']
    // 1. 提取所有不重複的房型名稱 (roomTitle)
    const titles = [...new Set(state.selectedBookingRoomsList.map((item) => item.room.roomTitle))]
    const roomTitleJoined = titles.join(' & ')
    const stayRange = formatHotelStayRange({ bookings: state.selectedBookingRoomsList, showCheckoutDate })
    const stay = resolveHotelStay({ bookings: state.selectedBookingRoomsList, showCheckoutDate })

    return {
      roomTitle: roomTitleJoined,
      dateRange: stayRange,
      nights: stay?.nights || 0,
    }
  },
  subTotal: (state, getters) => {
    console.log(state.calculateOrder)
    const detailedBookingListPrice = getters.detailedBookingList.reduce((acc, cur) => acc + cur.price, 0) || 0
    const adjustmentsPrice = state.calculateOrder?.priceRuleAdjustment.reduce((acc, cur) => acc + cur.price, 0) || 0
    const extraGuestFee = state.calculateOrder?.extraGuestFee.reduce((acc, cur) => acc + cur.price * cur.count, 0) || 0

    return detailedBookingListPrice + adjustmentsPrice + extraGuestFee
  },
  priceAdjustments: (state) => state.priceAdjustments,
  addonItems: (state) => state.addonItems,
  orderNote: (state) => state.orderNote,
  adminBackfillMode: (state) => state.adminBackfillMode,
}

const mutations = {
  SET_ORDER(state, order) {
    state.currentOrder = order
  },
  SET_LOADING(state, status) {
    state.loading = status
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_BOOKING(state, roomList) {
    state.selectedBookingRoomsList = roomList
  },
  SET_MEMBER(state, member) {
    state.currentMemberInfo = member
  },
  SET_MAXACCOMMODATES(state, max) {
    state.currentMaxAccommodates = max
  },
  SET_PETS(state, pets) {
    state.selectedPets = pets
  },
  SET_CALCULATE(state, order) {
    state.calculateOrder = order
  },
  SET_PRICEADJUSTMENT(state, price) {
    state.priceAdjustments = [...price]
  },
  SET_ADDONITEMS(state, items) {
    state.addonItems = [...items]
  },
  RESET_STATE(state) {
    const backfill = state.adminBackfillMode
    Object.assign(state, getDefaultState())
    state.adminBackfillMode = backfill
  },
  SET_ADMIN_BACKFILL_MODE(state, value) {
    state.adminBackfillMode = !!value
  },
  SET_NOTE(state, note) {
    state.orderNote = note
  },
}

const actions = {
  async fetchOrder({ commit }, { productId, containerId }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      console.log('ACTION', productId, containerId)
      const order = await getOrder(productId, containerId)
      commit('SET_ORDER', order)
      return order
    } catch (err) {
      commit('SET_ERROR', err)
      throw err
    } finally {
      commit('SET_LOADING', false)
    }
  },
  async fetchList({ commit }, query) {
    return request.get('/admin/hotel/order', { params: query }).then((response) => {
      // API 回傳: { data: [...], pagination: {...} } 或 { items: [...], pagination: {...} }
      const items = response.items ?? response.data ?? []
      const rawPagination = response.pagination || response.meta || {}
      const pagination = {
        ...rawPagination,
        total: rawPagination.total ?? rawPagination.total_count ?? 0,
        perPage: Number(rawPagination.perPage ?? rawPagination.per_page ?? rawPagination.limit) || 15,
      }
      return {
        items,
        pagination,
      }
    })
  },
  /**
   * GET /admin/member/{member_id}/hotel-orders — 結構同 fetchList
   * 會員端 API 使用 perPage；若傳入 limit（與 pageMixin 一致）則轉成 perPage
   */
  async fetchMemberList({ commit }, { memberId, ...query }) {
    const params = { ...query }
    if (params.limit != null && params.perPage == null) {
      params.perPage = params.limit
      delete params.limit
    }
    return request.get(`/admin/member/${memberId}/hotel-orders`, { params }).then((response) => {
      const items = response.items ?? response.data ?? []
      const rawPagination = response.pagination || response.meta || {}
      const pagination = {
        ...rawPagination,
        total: rawPagination.total ?? rawPagination.total_count ?? 0,
        perPage: Number(rawPagination.perPage ?? rawPagination.per_page ?? rawPagination.limit) || 15,
      }
      return {
        items,
        pagination,
      }
    })
  },
  async export({ commit }, exportParams) {
    return request.get('/admin/hotel/order/export', { params: exportParams }).then((response) => {
      // API response: { fileName: '...', csvData: [...] }
      return {
        fileName: response.fileName || 'hotel_orders_export.csv',
        csvData: response.csvData || [],
      }
    })
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
